import { useState, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { uploadFileViaSignedUrl } from '../services/uploadService';

interface UseSignedUploadOptions {
  /** Called after the file is uploaded to GCS with the fileId */
  onUploaded?: (fileId: string) => Promise<void> | void;
}

interface UseSignedUploadReturn {
  /** Upload a file via signed URL. Returns the GCS fileId. */
  upload: (file: Blob, fileName: string, contentType: string) => Promise<string>;
  /** 0-100 upload progress */
  progress: number;
  /** Whether an upload is in flight */
  uploading: boolean;
  /** Error message if the upload failed */
  error: string | null;
  /** Reset state (progress, error) */
  reset: () => void;
}

export const useSignedUpload = (options?: UseSignedUploadOptions): UseSignedUploadReturn => {
  const { user } = useAuth();
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortedRef = useRef(false);

  const reset = useCallback(() => {
    setProgress(0);
    setUploading(false);
    setError(null);
    abortedRef.current = false;
  }, []);

  const upload = useCallback(
    async (file: Blob, fileName: string, contentType: string): Promise<string> => {
      if (!user) throw new Error('You must be signed in to upload files');

      setUploading(true);
      setError(null);
      setProgress(0);
      abortedRef.current = false;

      try {
        const token = await user.getIdToken();
        const fileId = await uploadFileViaSignedUrl(
          token,
          file,
          fileName,
          contentType,
          (pct) => {
            if (!abortedRef.current) setProgress(pct);
          }
        );

        if (options?.onUploaded) {
          await options.onUploaded(fileId);
        }

        setProgress(100);
        return fileId;
      } catch (err: any) {
        const message = err.message || 'Upload failed. Please try again.';
        setError(message);
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [user, options]
  );

  return { upload, progress, uploading, error, reset };
};
